import { createRouter as createVueRouter, createWebHistory, type Router, type RouteLocationNormalized } from 'vue-router'
import { authGuard } from '@auth0/auth0-vue'

export const ROUTE_NAMES = {
  LOGIN: 'login',
  DASHBOARD: 'dashboard',
  ACCOUNT: 'account',
  ALERT: 'alert',
  ALERTS: 'alerts',
  ASN_DETAILS: 'asn_details',
  BLOCKED: 'blocked',
  COUNTRY_DETAILS: 'country_details',
  ERROR: 'error',
  FEED: 'feed',
  FEED_CREATE: 'feed_create',
  FEED_EDIT: 'feed_edit',
  FEEDS: 'feeds',
  FILES: 'files',
  FLOWS: 'flows',
  INACTIVE: 'inactive',
  IP_DETAILS: 'ip_details',
  LOGS: 'logs',
  LOGOUT: 'logout',
  NETWORK_DETAILS: 'network_details',
  NOT_FOUND: 'not_found',
  REPORT_RUN: 'report_run',
  REPORT_VIEW: 'report_view',
  REPORT: 'report',
  REPORTS: 'reports',
  SENTINEL: 'sentinel',
  SENTINELS: 'sentinels',
  SETTINGS: 'settings',
  SIGNAL: 'signal',
  SIGNALS: 'signals',
  SLACK: 'slack'
}

export function appendPageTitle(to: RouteLocationNormalized) {
  document.title = `Nomic HQ | ${to.meta['title']}`
}

export function createRouter() {
  const routes = [
    {
      path: '/',
      beforeEnter: authGuard,
      component: () => import('@/components/AppLayout.vue'),
      children: [
        {
          path: 'account',
          name: ROUTE_NAMES.ACCOUNT,
          meta: { title: 'Account' },
          props: true,
          component: () => import('@/views/AccountView.vue')
        },
        {
          path: 'alerts',
          name: ROUTE_NAMES.ALERTS,
          meta: { title: 'Alerts' },
          props: true,
          component: () => import('@/views/AlertListView.vue')
        },
        {
          path: 'asn/:asn/',
          name: ROUTE_NAMES.ASN_DETAILS,
          meta: { title: 'ASN Details' },
          props: (route: RouteLocationNormalized) => ({
            ...route.params,
            ...{ asn: parseInt(route.params.asn as string) }
          }),
          component: () => import('@/views/IPDetailsView.vue')
        },
        {
          path: 'country/:cc/',
          name: ROUTE_NAMES.COUNTRY_DETAILS,
          meta: { title: 'Country Details' },
          props: true,
          component: () => import('@/views/IPDetailsView.vue')
        },
        {
          path: 'dashboard',
          name: ROUTE_NAMES.DASHBOARD,
          meta: { title: 'Dashboard' },
          props: true,
          component: () => import('@/views/DashboardView.vue')
        },
        {
          path: 'feeds',
          name: ROUTE_NAMES.FEEDS,
          meta: { title: 'Feeds' },
          props: true,
          component: () => import('@/views/FeedListView.vue')
        },
        {
          path: 'feeds/create',
          name: ROUTE_NAMES.FEED_CREATE,
          meta: { title: 'Create Feed' },
          props: true,
          component: () => import('@/views/FeedCreateView.vue')
        },
        {
          path: 'feeds/:feedId',
          name: ROUTE_NAMES.FEED,
          meta: { title: 'Feed Details', orgScoped: true },
          props: true,
          component: () => import('@/views/FeedDetailView.vue')
        },
        {
          path: 'feeds/:feedId/edit',
          name: ROUTE_NAMES.FEED_EDIT,
          meta: { title: 'Edit Feed', orgScoped: true },
          props: true,
          component: () => import('@/views/FeedEditView.vue')
        },
        {
          path: 'files',
          name: ROUTE_NAMES.FILES,
          meta: { title: 'Files' },
          props: true,
          component: () => import('@/views/FileListView.vue')
        },
        {
          path: 'flows',
          name: ROUTE_NAMES.FLOWS,
          meta: { title: 'Flows', insightOnly: true },
          props: true,
          component: () => import('@/views/FlowListView.vue')
        },
        {
          path: 'logs',
          name: ROUTE_NAMES.LOGS,
          meta: { title: 'Logs' },
          props: true,
          component: () => import('@/views/AuditLogListView.vue')
        },
        {
          path: 'network/:network/',
          name: ROUTE_NAMES.IP_DETAILS,
          meta: { title: 'Network Details' },
          props: true,
          component: () => import('@/views/IPDetailsView.vue')
        },
        // Reports are created straight from the form now, so there is no
        // preview screen to route to — only bookmarks and stale links still
        // aim here, and the list is where they belong (#661). Must stay ahead
        // of `reports/:id` or "preview" is read as a report id.
        {
          path: 'reports/preview',
          redirect: () => ({ name: ROUTE_NAMES.REPORTS })
        },
        {
          path: 'reports/:id/runs/:runId',
          name: ROUTE_NAMES.REPORT_RUN,
          meta: { title: 'Report Run', orgScoped: true },
          props: true,
          component: () => import('@/views/ReportRunView.vue')
        },
        {
          path: 'reports/:id/view',
          name: ROUTE_NAMES.REPORT_VIEW,
          meta: { title: 'Report View', orgScoped: true },
          props: true,
          component: () => import('@/views/ReportView.vue')
        },
        {
          path: 'reports/:id',
          name: ROUTE_NAMES.REPORT,
          meta: { title: 'Report Details', orgScoped: true },
          props: true,
          component: () => import('@/views/ReportDetailView.vue')
        },
        {
          path: 'reports',
          name: ROUTE_NAMES.REPORTS,
          meta: { title: 'Reports' },
          props: true,
          component: () => import('@/views/ReportListView.vue')
        },
        {
          path: 'sentinels',
          name: ROUTE_NAMES.SENTINELS,
          meta: { title: 'Sentinels' },
          props: true,
          component: () => import('@/views/SentinelListView.vue')
        },
        {
          path: 'sentinels/:sentinelId',
          name: ROUTE_NAMES.SENTINEL,
          meta: { title: 'Sentinel Details', orgScoped: true },
          props: true,
          component: () => import('@/views/SentinelDetailsView.vue')
        },
        {
          path: 'settings',
          name: ROUTE_NAMES.SETTINGS,
          meta: { title: 'Settings' },
          component: () => import('@/views/SettingsView.vue')
        },
        {
          path: 'signals/:id/fork',
          name: 'signals-fork',
          meta: { title: 'Fork Signal', orgScoped: true },
          props: (route: RouteLocationNormalized) => ({ ...route.params, fork: true }),
          component: () => import('@/views/SignalDetailsView.vue')
        },
        {
          path: 'signals/:id',
          name: ROUTE_NAMES.SIGNAL,
          meta: { title: 'Signal Details', orgScoped: true },
          props: true,
          component: () => import('@/views/SignalDetailsView.vue')
        },
        {
          path: 'signals',
          name: ROUTE_NAMES.SIGNALS,
          meta: { title: 'Signals' },
          props: true,
          component: () => import('@/views/SignalListView.vue')
        },
        {
          path: 'slack',
          name: ROUTE_NAMES.SLACK,
          meta: { title: 'Slack' },
          component: () => import('@/views/SlackRedirect.vue')
        }
      ]
    },
    {
      path: '/sandbox',
      name: 'sandbox',
      meta: { title: 'Sandbox' },
      component: () => import('@/views/SandboxView.vue')
    },
    {
      path: '/callback',
      name: 'callback',
      meta: { title: 'Initializing Nomic HQ...' },
      beforeEnter: authGuard,
      component: () => import('@/views/CallbackView.vue')
    },
    {
      path: '/inactive',
      name: ROUTE_NAMES.INACTIVE,
      meta: { title: 'Account Inactive' },
      component: () => import('@/views/Error403View.vue')
    },
    {
      path: '/logout',
      name: ROUTE_NAMES.LOGOUT,
      meta: { title: 'Logout' },
      component: () => import('@/views/LogoutView.vue')
    },
    {
      path: '/blocked',
      name: ROUTE_NAMES.BLOCKED,
      meta: { title: 'Blocked' },
      component: () => import('@/views/ErrorBlockedView.vue')
    },
    {
      path: '/404',
      name: ROUTE_NAMES.NOT_FOUND,
      meta: { title: 'Page Not Found' },
      component: () => import('@/views/Error404View.vue')
    },
    {
      path: '/500',
      name: ROUTE_NAMES.ERROR,
      meta: { title: 'Internal Error' },
      component: () => import('@/views/Error500View.vue')
    },
    // Treat the dashboard as the default route
    {
      path: '',
      redirect: () => ({ name: 'callback' })
    },
    // App level redirect
    {
      path: '/:pathMatch(.*)*',
      // empty params object prevents vue router warning
      // see: https://github.com/vuejs/router/issues/1617
      redirect: () => ({ name: 'callback', params: {} })
    }
  ]

  return createVueRouter({
    routes,
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      return savedPosition ?? { top: 0 }
    }
  })
}

export const setPageTitleGuard = (router: Router) => {
  router.afterEach(appendPageTitle)
}
